/**
 * SidebetCancelButtons
 *
 * Manages one CancelBetOverlay button per PLACED sidebet zone the local
 * player owns while that zone is still completely unfaded. Tapping asks the
 * server to cancel (game:bet_zone_cancel) and refund the stake; the button
 * disappears the moment any fade lands on the zone (server is the race
 * authority — the client only mirrors `fadedAmount === 0`).
 *
 * Distinct from the single pendingCreate cancel button (owned by
 * BettingController/GameFlowController): that one cancels a bet that never
 * reached the server. These cancel a bet the server already accepted.
 */

import * as THREE from 'three';
import { CancelBetOverlay } from './CancelBetOverlay';

export interface SidebetCancelButtonsConfig {
  container: HTMLElement;
  camera: THREE.PerspectiveCamera;
  canvas: HTMLCanvasElement;
}

export interface CancellableZoneEntry {
  zoneId: string;
  position: { x: number; y: number; z: number };
}

export class SidebetCancelButtons {
  private config: SidebetCancelButtonsConfig;
  private buttons = new Map<string, CancelBetOverlay>();
  private pool: CancelBetOverlay[] = [];
  private onCancel: ((zoneId: string) => void) | null = null;

  constructor(config: SidebetCancelButtonsConfig) {
    this.config = config;
  }

  setOnCancel(cb: ((zoneId: string) => void) | null): void {
    this.onCancel = cb;
  }

  /**
   * Idempotent reconcile to the target set: new zones get a (pooled) button,
   * existing ones re-anchor (positions are static, but this keeps the call
   * safe to repeat), stale ones hide and return to the pool.
   */
  sync(entries: CancellableZoneEntry[]): void {
    const targetIds = new Set(entries.map((e) => e.zoneId));
    for (const [zoneId, button] of this.buttons) {
      if (!targetIds.has(zoneId)) {
        button.hide();
        this.buttons.delete(zoneId);
        this.pool.push(button);
      }
    }
    for (const entry of entries) {
      let button = this.buttons.get(entry.zoneId);
      if (!button) {
        button = this.pool.pop() ?? new CancelBetOverlay(this.config);
        this.buttons.set(entry.zoneId, button);
        const id = entry.zoneId;
        button.setOnCancel(() => this.onCancel?.(id));
        button.setAnchorPosition(entry.position);
        button.show();
      } else {
        button.setAnchorPosition(entry.position);
      }
    }
  }

  /** Immediately drop one zone's button (optimistic hide on tap). */
  hide(zoneId: string): void {
    const button = this.buttons.get(zoneId);
    if (!button) return;
    button.hide();
    this.buttons.delete(zoneId);
    this.pool.push(button);
  }

  hideAll(): void {
    this.sync([]);
  }

  /** Per-frame reprojection while the camera moves. */
  update(): void {
    for (const button of this.buttons.values()) {
      button.update();
    }
  }

  destroy(): void {
    for (const button of this.buttons.values()) button.destroy();
    for (const button of this.pool) button.destroy();
    this.buttons.clear();
    this.pool = [];
    this.onCancel = null;
  }
}
